
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import {
    Box, Typography, Card, CardContent, CardActionArea, Chip, Grid,
    Skeleton, Button, Paper, Avatar, Fade, LinearProgress, useTheme
} from '@mui/material';
import api from '../../services/api';
import { Parcours } from '../../types/course';
import { useNavigate } from 'react-router-dom';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SchoolIcon from '@mui/icons-material/School';
import SearchIcon from '@mui/icons-material/Search';
import InputBase from '@mui/material/InputBase';
import LayersIcon from '@mui/icons-material/Layers';
import LottieLoading from '../../components/common/LottieLoading';

const MyParcours = () => {
    const [parcoursList, setParcoursList] = useState<Parcours[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const { user } = useContext(AuthContext) || {};
    const navigate = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        fetchParcours();
    }, []);



    const fetchParcours = async () => {
        try {
            if (user?.role === 'super-admin') {
                // Super admin sees ALL parcours
                const res = await api.get('/api/course/parcours');
                setParcoursList(res.data);
            } else {
                // Learner sees only enrolled parcours
                const [enrollRes, progressRes] = await Promise.all([
                    api.get('/api/enrollment/active'),
                    api.get('/api/progress/parcours')
                ]);

                // Create a map of progress for quick lookup
                const progressMap = new Map();
                progressRes.data.forEach((p: any) => progressMap.set(p.parcoursId, p.progress));

                const enrolledParcours = enrollRes.data.map((enrollment: any) => ({
                    ...enrollment.parcours,
                    progress: progressMap.get(enrollment.parcours?._id) || 0
                }));
                setParcoursList(enrolledParcours);
            }
            setLoading(false);
        } catch (err) {
            console.error("Error fetching parcours", err);
            setLoading(false);
        }
    };

    const filteredParcours = parcoursList.filter(p =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <LottieLoading message="Chargement de vos parcours..." animationType="learner" />;

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            p: { xs: 2, md: 4 }
        }}>
            {/* Hero Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                    p: { xs: 3, md: 5 },
                    mb: 5,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{
                    position: 'absolute',
                    top: -60,
                    right: -60,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                }} />
                <Box sx={{
                    position: 'absolute',
                    bottom: -30,
                    left: 40,
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.08)',
                }} />

                <Grid container spacing={4} alignItems="center">
                    <Grid size={{ xs: 12, md: 7 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                width: 56,
                                height: 56,
                                backdropFilter: 'blur(10px)'
                            }}>
                                <SchoolIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif', fontSize: { xs: '2rem', md: '3rem' } }}>
                                    Mes Parcours
                                </Typography>
                                <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 500, fontSize: '1.1rem' }}>
                                    Continuez votre voyage d'apprentissage et atteignez vos objectifs.
                                </Typography>
                            </Box>
                        </Box>

                        {/* Search Bar inside Hero */}
                        <Paper
                            elevation={0}
                            sx={{
                                mt: 3,
                                p: '4px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                maxWidth: 450,
                                borderRadius: 3,
                                bgcolor: 'rgba(255,255,255,0.15)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                transition: 'all 0.3s',
                                '&:focus-within': {
                                    bgcolor: 'rgba(255,255,255,1)',
                                    '& .MuiInputBase-input': { color: '#2c3e50' },
                                    '& .search-icon': { color: '#667eea' }
                                }
                            }}
                        >
                            <Box className="search-icon" sx={{ p: 1, color: 'white' }}>
                                <SearchIcon />
                            </Box>
                            <InputBase
                                sx={{ ml: 1, flex: 1, fontFamily: 'Quicksand, sans-serif', color: 'white' }}
                                placeholder="Rechercher un parcours..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Box sx={{
                            bgcolor: 'rgba(255,255,255,0.1)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: 4,
                            p: 3,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%'
                        }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h2" sx={{ fontWeight: 900, mb: 1 }}>{parcoursList.length}</Typography>
                                <Typography variant="body1" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
                                    Parcours assignés
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Content */}
            {filteredParcours.length === 0 ? (
                <Fade in timeout={500}>
                    <Paper
                        sx={{
                            p: 8,
                            textAlign: 'center',
                            borderRadius: 4,
                            background: 'white',
                            border: '2px dashed #e0e0e0',
                        }}
                    >
                        <SchoolIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#666', mb: 1 }}>
                            Aucun parcours trouvé
                        </Typography>
                        <Typography color="text.secondary">
                            {searchTerm ? "Ajustez votre recherche" : "Vous n'êtes inscrit à aucun parcours pour le moment"}
                        </Typography>
                    </Paper>
                </Fade>
            ) : (
                <Grid container spacing={4}>
                    {filteredParcours.map((parcours, index) => {
                        // Extract categories for display (first 3 unique ones from all blocs)
                        const allCategories = parcours.blocs?.flatMap(b => b.categories?.map(c => c.title) || []) || [];
                        const distinctCategories = Array.from(new Set(allCategories)).slice(0, 3);

                        return (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={parcours._id}>
                                <Fade in timeout={500 + index * 100}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            borderRadius: 4,
                                            border: '1px solid rgba(0,0,0,0.05)',
                                            boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                transform: 'translateY(-10px)',
                                                boxShadow: '0 20px 40px rgba(102, 126, 234, 0.15)',
                                                '& .course-bg': {
                                                    transform: 'scale(1.1) rotate(2deg)',
                                                }
                                            }
                                        }}
                                    >
                                        <CardActionArea
                                            onClick={() => navigate(`/learner/parcours/${parcours._id}`)}
                                            sx={{
                                                flexGrow: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    height: 160,
                                                    background: `linear-gradient(135deg, ${index % 2 === 0 ? '#667eea 0%, #764ba2' : '#2193b0 0%, #6dd5ed'} 100%)`,
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {/* Decorative Background */}
                                                <Box
                                                    className="course-bg"
                                                    sx={{
                                                        position: 'absolute',
                                                        width: '120%',
                                                        height: '120%',
                                                        background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
                                                        transition: 'transform 0.5s ease',
                                                        pointerEvents: 'none'
                                                    }}
                                                />

                                                <Avatar
                                                    sx={{
                                                        width: 64,
                                                        height: 64,
                                                        bgcolor: 'rgba(255,255,255,0.2)',
                                                        backdropFilter: 'blur(10px)',
                                                        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                                                        color: 'white'
                                                    }}
                                                >
                                                    <LayersIcon sx={{ fontSize: 30 }} />
                                                </Avatar>
                                            </Box>

                                            <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                                                <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    <Chip
                                                        label={`${parcours.blocs?.length || 0} Blocs`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: 'rgba(102, 126, 234, 0.08)',
                                                            color: '#667eea',
                                                            fontWeight: 600,
                                                            borderRadius: 1.5
                                                        }}
                                                    />
                                                    {distinctCategories.map((cat, i) => (
                                                        <Chip
                                                            key={i}
                                                            label={cat}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#f5f7fa',
                                                                color: 'text.secondary',
                                                                fontSize: '0.7rem',
                                                                borderRadius: 1.5
                                                            }}
                                                        />
                                                    ))}
                                                </Box>

                                                <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif', color: '#2c3e50', mb: 1, lineHeight: 1.3 }}>
                                                    {parcours.title}
                                                </Typography>

                                                <Box sx={{ mt: 'auto', width: '100%' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#667eea' }}>
                                                            {Math.round(parcours.progress || 0)}% complété
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={parcours.progress || 0}
                                                        sx={{
                                                            height: 8,
                                                            borderRadius: 4,
                                                            mb: 2,
                                                            bgcolor: 'rgba(102, 126, 234, 0.1)',
                                                            '& .MuiLinearProgress-bar': {
                                                                borderRadius: 4,
                                                                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                                                            }
                                                        }}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        endIcon={<PlayArrowIcon />}
                                                        fullWidth
                                                        sx={{
                                                            borderRadius: 2.5,
                                                            textTransform: 'none',
                                                            fontWeight: 700,
                                                            py: 1,
                                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
                                                            '&:hover': {
                                                                boxShadow: '0 6px 15px rgba(102, 126, 234, 0.3)',
                                                            }
                                                        }}
                                                    >
                                                        Continuer l'apprentissage
                                                    </Button>
                                                </Box>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Fade>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </Box>
    );
};

export default MyParcours;

